// src/screens/inventory/InventoryMetrics.js
import React, { useState, useMemo } from "react";
import {
  Package,
  TrendingUp,
  AlertTriangle,
  Star,
  BarChart3,
  PieChart,
  DollarSign,
  ShoppingBag,
  Layers,
  Filter,
  Eye,
  EyeOff,
  PackageOpen,
  Calendar,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from "lucide-react";

const getSampleOrderRefs = (order) => {
  const arrays = [
    order?.items,
    order?.samples,
    order?.sampleItems,
    order?.sampleSelections,
    order?.selectedSamples,
    order?.lineItems,
  ].filter(Array.isArray);

  if (arrays.length > 0) {
    return arrays[0]
      .map((item) => ({
        id: item?.inventoryId || item?.sampleId || item?.itemId || item?.id || null,
        name: item?.name || item?.title || item?.itemName || null,
        qty: Number(item?.quantity ?? item?.qty ?? item?.count ?? 1) || 1,
      }))
      .filter((item) => item.id || item.name);
  }

  const idArrays = [order?.sampleIds, order?.inventoryIds, order?.itemIds].filter(Array.isArray);
  if (idArrays.length > 0) {
    return idArrays[0]
      .map((id) => ({ id: id ? String(id) : null, name: null, qty: 1 }))
      .filter((item) => item.id);
  }

  return [];
};

const InventoryMetrics = ({ inventory, sampleOrders, getProductSampleCount }) => {
  // NOTE: this is the filter segment (not a timeframe)
  const [segment, setSegment] = useState("all");
  const [showHidden, setShowHidden] = useState(false);

  // ---------- Helpers ----------
  const isLowStock = (item) => {
    const stock = typeof item?.stock === "number" ? item.stock : null;
    const threshold =
      typeof item?.lowStockThreshold === "number" ? item.lowStockThreshold : null;

    if (stock === null || threshold === null) return false;
    return stock <= threshold;
  };

  // 1) Filter by segment (All / Sample-ready / Featured / Low Stock)
  const scopedInventory = useMemo(() => {
    const base = Array.isArray(inventory) ? inventory : [];

    switch (segment) {
      case "sampleReady":
        return base.filter((item) => !!item.sampleable);
      case "featured":
        return base.filter((item) => !!item.featured);
      case "lowstock":
        return base.filter((item) => isLowStock(item));
      case "all":
      default:
        return base;
    }
  }, [inventory, segment]);

  // 2) Optionally hide hidden-from-customer items
  const analysisInventory = useMemo(() => {
    if (showHidden) return scopedInventory;
    return scopedInventory.filter((item) => !item?.hiddenFromCustomer);
  }, [scopedInventory, showHidden]);

  // Calculate comprehensive metrics (NOW based on filtered inventory)
  const metrics = useMemo(() => {
    const data = Array.isArray(analysisInventory) ? analysisInventory : [];
    const scopeHiddenCount = (Array.isArray(scopedInventory) ? scopedInventory : []).filter(
      (i) => !!i?.hiddenFromCustomer
    ).length;

    const totalProducts = data.length;

    // If filter results in 0 items, return safe defaults (prevents NaN/Infinity)
    if (!totalProducts) {
      return {
        totalProducts: 0,
        sampleableProducts: 0,
        featuredProducts: 0,
        hiddenProducts: scopeHiddenCount, // hidden count in scope, even if not shown
        lowStockProducts: 0,
        outOfStockProducts: 0,
        totalInventoryValue: 0,
        avgStockValue: 0,
        avgStockLevel: 0,
        categoryStats: {},
        priceRange: { min: 0, max: 0, avg: 0 },
        totalSamples: sampleOrders?.length || 0,
        sampledProductPercentage: 0,
        topSampledProducts: [],
        topValueProducts: [],
        recentlyUpdated: 0,
        productSampleCounts: [],
        totalSampleOrderedValue: 0,
        sampleValueCoveragePct: 0
      };
    }

    const sampleableProducts = data.filter((item) => !!item.sampleable).length;
    const featuredProducts = data.filter((item) => !!item.featured).length;

    // Hidden count within the current segment scope (even if showHidden is off)
    const hiddenProducts = scopeHiddenCount;

    // Stock metrics
    const lowStockProducts = data.filter((item) => isLowStock(item)).length;

    const outOfStockProducts = data.filter((item) => {
      const stock = typeof item?.stock === "number" ? item.stock : null;
      return stock !== null && stock <= 0;
    }).length;

    // Value metrics
    const totalInventoryValue = data.reduce((sum, item) => {
      return sum + (item.price || 0) * (item.stock || 0);
    }, 0);

    const avgStockValue = totalProducts > 0 ? totalInventoryValue / totalProducts : 0;

    const avgStockLevel =
      totalProducts > 0
        ? data.reduce((sum, item) => sum + (item.stock || 0), 0) / totalProducts
        : 0;

    // Category analysis
    const categoryStats = data.reduce((acc, item) => {
      const category = item.category || "Uncategorized";
      if (!acc[category]) {
        acc[category] = {
          count: 0,
          value: 0,
          avgPrice: 0,
          totalStock: 0
        };
      }
      acc[category].count++;
      acc[category].value += (item.price || 0) * (item.stock || 0);
      acc[category].totalStock += item.stock || 0;

      // Guard against divide-by-zero
      acc[category].avgPrice =
        acc[category].totalStock > 0 ? acc[category].value / acc[category].totalStock : 0;

      return acc;
    }, {});

    // Price analysis (safe min when no positive prices)
    const positivePrices = data.map((item) => item.price || 0).filter((p) => p > 0);
    const priceRange = {
      min: positivePrices.length ? Math.min(...positivePrices) : 0,
      max: Math.max(...data.map((item) => item.price || 0)),
      avg: data.reduce((sum, item) => sum + (item.price || 0), 0) / totalProducts
    };

    // Sample performance
    const productSampleCounts = data
      .map((item) => ({
        ...item,
        sampleCount: getProductSampleCount(item.id)
      }))
      .filter((item) => item.sampleCount > 0);

    const inventoryById = new Map(data.map((item) => [String(item.id || ""), item]));
    const inventoryByName = new Map(
      data
        .map((item) => [String(item.name || "").trim().toLowerCase(), item])
        .filter(([name]) => name)
    );

    const totalSampleOrderedValue = (Array.isArray(sampleOrders) ? sampleOrders : []).reduce((sum, order) => {
      const refs = getSampleOrderRefs(order);
      return (
        sum +
        refs.reduce((orderSum, ref) => {
          const byId = ref.id ? inventoryById.get(String(ref.id)) : null;
          const byName = !byId && ref.name ? inventoryByName.get(String(ref.name).trim().toLowerCase()) : null;
          const item = byId || byName;
          const price = Number(item?.price || 0);
          const qty = Number(ref.qty || 1) || 1;
          return orderSum + price * qty;
        }, 0)
      );
    }, 0);

    const totalSamples = sampleOrders?.length || 0;

    const sampledProductPercentage =
      totalProducts > 0 ? (productSampleCounts.length / totalProducts) * 100 : 0;

    const sampleValueCoveragePct =
      totalInventoryValue > 0 ? (totalSampleOrderedValue / totalInventoryValue) * 100 : 0;

    // Top performers
    const topSampledProducts = [...productSampleCounts]
      .sort((a, b) => b.sampleCount - a.sampleCount)
      .slice(0, 5);

    const topValueProducts = [...data]
      .filter((item) => (item.price || 0) > 0 && (item.stock || 0) > 0)
      .sort((a, b) => b.price * b.stock - a.price * a.stock)
      .slice(0, 5);

    // Recently updated (within last 7 days)
    const recentlyUpdated = data.filter((item) => {
      if (!item.updatedAt) return false;
      const updatedDate = item.updatedAt.toDate ? item.updatedAt.toDate() : new Date(item.updatedAt);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return updatedDate > sevenDaysAgo;
    }).length;

    return {
      totalProducts,
      sampleableProducts,
      featuredProducts,
      hiddenProducts,
      lowStockProducts,
      outOfStockProducts,
      totalInventoryValue,
      avgStockValue,
      avgStockLevel,
      categoryStats,
      priceRange,
      totalSamples,
      sampledProductPercentage,
      topSampledProducts,
      topValueProducts,
      recentlyUpdated,
      productSampleCounts,
      totalSampleOrderedValue,
      sampleValueCoveragePct
    };
  }, [analysisInventory, scopedInventory, sampleOrders, getProductSampleCount]);

  // Color utility
  const getStockStatusColor = (stock, threshold) => {
    if (!stock || stock <= 0) return "bg-red-100 text-red-700 border-red-200";
    if (!threshold || stock > threshold) return "bg-green-100 text-green-700 border-green-200";
    return "bg-yellow-100 text-yellow-700 border-yellow-200";
  };

  const getTrendColor = (value, comparison) => {
    if (value > comparison) return "text-green-600";
    if (value < comparison) return "text-red-600";
    return "text-gray-600";
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value) => {
    return `${Math.round(value)}%`;
  };

  if (!inventory || inventory.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Inventory Data</h3>
        <p className="text-gray-500">Add products to your inventory to see analytics.</p>
      </div>
    );
  }

  const FILTERS = [
    { key: "all", label: "All" },
    { key: "sampleReady", label: "Sample-ready" }, // renamed (was Sampleable)
    { key: "featured", label: "Featured" },
    { key: "lowstock", label: "Low Stock" }
  ];

  return (
    <div className="space-y-4">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Inventory Analytics</h2>
            <p className="text-xs text-gray-500">{metrics.totalProducts || 0} products in view</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setSegment(f.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  segment === f.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowHidden(!showHidden)}
            className={`p-2 rounded-lg border ${
              showHidden ? "bg-blue-50 text-blue-600 border-blue-200" : "bg-gray-50 text-gray-600 border-gray-200"
            }`}
            title={showHidden ? "Hide hidden products" : "Show hidden products"}
          >
            {showHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Key Metrics Grid - Compact & Visual */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
        {/* Total Value */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-md bg-blue-600/20 flex items-center justify-center">
                  <DollarSign className="w-3 h-3 text-blue-600" />
                </div>
                <span className="text-xs font-medium text-blue-700">Total Value</span>
              </div>
              <div className="text-lg font-bold text-gray-900">
                {formatCurrency(metrics.totalInventoryValue || 0)}
              </div>
            </div>
            <div className="text-xs text-gray-500">{metrics.totalProducts || 0} items</div>
          </div>
          <div className="mt-3 text-[11px] text-gray-600">
            Avg: {formatCurrency(metrics.avgStockValue || 0)} per product
          </div>
        </div>

        {/* Stock Health */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-md bg-green-600/20 flex items-center justify-center">
                  <PackageOpen className="w-3 h-3 text-green-600" />
                </div>
                <span className="text-xs font-medium text-green-700">Stock Health</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{Math.round(metrics.avgStockLevel || 0)} avg</div>
            </div>
            <div className="flex items-center gap-2">
              {metrics.lowStockProducts > 0 ? (
                <div className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                  {metrics.lowStockProducts} low
                </div>
              ) : (
                <div className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Good</div>
              )}
            </div>
          </div>
          <div className="mt-3 text-[11px] text-gray-600">{metrics.outOfStockProducts || 0} out of stock</div>
        </div>

        {/* Sample Performance */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-md bg-purple-600/20 flex items-center justify-center">
                  <ShoppingBag className="w-3 h-3 text-purple-600" />
                </div>
                <span className="text-xs font-medium text-purple-700">Sample Rate</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{formatPercent(metrics.sampledProductPercentage || 0)}</div>
            </div>
            <div className="text-xs text-gray-500">{metrics.productSampleCounts?.length || 0} sampled</div>
          </div>
          <div className="mt-3 text-[11px] text-gray-600">{metrics.totalSamples || 0} total samples</div>
        </div>

        {/* Featured Items */}
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-md bg-yellow-600/20 flex items-center justify-center">
                  <Star className="w-3 h-3 text-yellow-600" />
                </div>
                <span className="text-xs font-medium text-yellow-700">Featured</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{metrics.featuredProducts || 0}</div>
            </div>
            <div className="text-xs text-gray-500">
              {formatPercent(
                (metrics.totalProducts || 0) > 0 ? ((metrics.featuredProducts || 0) / (metrics.totalProducts || 1)) * 100 : 0
              )}
            </div>
          </div>
          <div className="mt-3 text-[11px] text-gray-600">{metrics.hiddenProducts || 0} hidden from customers</div>
        </div>

        {/* Sample Order Value */}
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-md bg-slate-700/10 flex items-center justify-center">
                  <ShoppingBag className="w-3 h-3 text-slate-700" />
                </div>
                <span className="text-xs font-medium text-slate-700">Sample Value</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{formatCurrency(metrics.totalSampleOrderedValue || 0)}</div>
            </div>
            <div className="text-xs text-gray-500">{formatPercent(metrics.sampleValueCoveragePct || 0)}</div>
          </div>
          <div className="mt-3 text-[11px] text-gray-600">Of current inventory value represented in sampled items</div>
        </div>
      </div>

      {/* Middle Section - Performance & Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Performing Products */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-semibold text-gray-900">Top Performing Products</h3>
            </div>
            <span className="text-xs text-gray-500">By samples requested</span>
          </div>

          <div className="space-y-2">
            {metrics.topSampledProducts?.length > 0 ? (
              metrics.topSampledProducts.map((product, index) => (
                <div
                  key={product.id}
                  className="flex items-center gap-3 rounded-xl bg-gray-50/80 px-3 py-2 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600">
                    {index + 1}
                  </div>
                  <div className="min-w-0 flex-1 text-sm font-medium text-gray-900 truncate">
                    {product.name}
                  </div>
                  <div className="hidden text-xs text-gray-500 sm:block truncate max-w-[120px]">
                    {product.category || "Uncategorized"}
                  </div>
                  {product.featured ? (
                    <Star className="h-3.5 w-3.5 shrink-0 text-yellow-500 fill-yellow-500" />
                  ) : null}
                  <div className="shrink-0 rounded-full bg-white px-2 py-1 text-[11px] font-semibold text-gray-700">
                    {product.sampleCount} samples
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <ShoppingBag className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No sample data available yet</p>
              </div>
            )}
          </div>

          {metrics.productSampleCounts?.length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
              {metrics.productSampleCounts.length} of {metrics.totalProducts} products ({formatPercent(metrics.sampledProductPercentage)}) have been
              sampled
            </div>
          )}
        </div>

        {/* Category Performance */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <PieChart className="w-4 h-4 text-purple-600" />
              <h3 className="text-sm font-semibold text-gray-900">Category Breakdown</h3>
            </div>
            <span className="text-xs text-gray-500">Products by category</span>
          </div>

          <div className="space-y-3">
            {Object.entries(metrics.categoryStats || {})
              .sort(([, a], [, b]) => b.count - a.count)
              .slice(0, 5)
              .map(([category, stats]) => {
                const percentage = metrics.totalProducts > 0 ? (stats.count / metrics.totalProducts) * 100 : 0;
                const valuePerItem = stats.count > 0 ? stats.value / stats.count : 0;

                return (
                  <div key={category} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                        <span className="text-sm font-medium text-gray-900">{category}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {stats.count} products ({formatPercent(percentage)})
                      </div>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-gradient-to-r from-purple-500 to-purple-600 h-1.5 rounded-full"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-gray-500">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          <span>Stock: {stats.totalStock}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          <span>{formatCurrency(valuePerItem)} avg</span>
                        </div>
                        <div className="text-gray-400">•</div>
                        <div className="flex items-center gap-1">
                          <Layers className="w-3 h-3" />
                          <span>{formatCurrency(stats.value)} total</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">{Object.keys(metrics.categoryStats || {}).length} categories total</span>
              <span className="text-gray-900 font-medium">{formatCurrency(metrics.totalInventoryValue || 0)} total value</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section - Stock Alerts & Value Leaders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Stock Alerts */}
        {metrics.lowStockProducts > 0 && (
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-orange-600 flex items-center justify-center">
                  <AlertTriangle className="w-3 h-3 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-orange-800">Stock Alerts</h3>
                  <p className="text-xs text-orange-600">
                    {metrics.lowStockProducts} product{metrics.lowStockProducts === 1 ? "" : "s"} need attention
                  </p>
                </div>
              </div>
              <span className="text-xs bg-orange-600 text-white px-2 py-1 rounded-full">Action Required</span>
            </div>

            <div className="space-y-2">
              {analysisInventory
                .filter((item) => isLowStock(item))
                .slice(0, 3)
                .map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-orange-900 truncate">{item.name}</div>
                      <div className="flex items-center gap-2 text-xs text-orange-700 mt-1">
                        <div className="flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          <span>Stock: {item.stock || 0}</span>
                        </div>
                        <div className="text-orange-400">•</div>
                        <div className="flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          <span>Threshold: {item.lowStockThreshold ?? "N/A"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-bold ${getTrendColor(item.stock, item.lowStockThreshold)}`}>
                        {(item.stock || 0) - (item.lowStockThreshold || 0)} left
                      </div>
                      <div className="text-[11px] text-orange-600">Below threshold</div>
                    </div>
                  </div>
                ))}

              {metrics.lowStockProducts > 3 && (
                <div className="text-center">
                  <button className="text-xs text-orange-600 hover:text-orange-800 font-medium">
                    + {metrics.lowStockProducts - 3} more items need reorder
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Highest Value Products */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-600" />
              <h3 className="text-sm font-semibold text-gray-900">Highest Value Products</h3>
            </div>
            <span className="text-xs text-gray-500">By inventory value</span>
          </div>

          <div className="space-y-3">
            {metrics.topValueProducts?.length > 0 ? (
              metrics.topValueProducts.map((product) => {
                const value = (product.price || 0) * (product.stock || 0);
                const stockStatus = getStockStatusColor(product.stock, product.lowStockThreshold);

                return (
                  <div
                    key={product.id}
                    className="flex items-center justify-between p-3 bg-gray-50/50 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                        <span className="text-xs font-bold text-green-600">${Math.round(value / 1000)}k</span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[160px]">{product.name}</div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className={stockStatus.split(" ")[2]}>Stock: {product.stock || 0}</span>
                          <span className="text-gray-400">•</span>
                          <span>${product.price || 0} ea</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-900">{formatCurrency(value)}</div>
                      <div className="text-[11px] text-gray-500">
                        {product.sampleable ? "Sample-ready" : "Not sample-ready"}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-4">
                <DollarSign className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No value data available</p>
              </div>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
            <div className="flex items-center justify-between">
              <span>
                Price range: {formatCurrency(metrics.priceRange?.min || 0)} - {formatCurrency(metrics.priceRange?.max || 0)}
              </span>
              <span>Avg: {formatCurrency(metrics.priceRange?.avg || 0)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Footer */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <Calendar className="w-3 h-3" />
            <span>Recently Updated</span>
          </div>
          <div className="text-sm font-semibold text-gray-900">{metrics.recentlyUpdated || 0} items</div>
        </div>

        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <Filter className="w-3 h-3" />
            <span>Hidden from Customers</span>
          </div>
          <div className="text-sm font-semibold text-gray-900">{metrics.hiddenProducts || 0} items</div>
        </div>

        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <TrendingUpIcon className="w-3 h-3" />
            <span>Sample-ready Products</span>
          </div>
          <div className="text-sm font-semibold text-gray-900">{metrics.sampleableProducts || 0} items</div>
        </div>

        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <TrendingDownIcon className="w-3 h-3" />
            <span>Out of Stock</span>
          </div>
          <div className="text-sm font-semibold text-gray-900">{metrics.outOfStockProducts || 0} items</div>
        </div>
      </div>
    </div>
  );
};

export default InventoryMetrics;
